import {
  ActivatedRouteSnapshot,
  RouteReuseStrategy,
  DetachedRouteHandle,
} from '@angular/router';

interface ReuseRouteOptions {
  path: string;
  param: string;
}

const makeReuseRouteOption = (path: string, param: string) =>
  ({
    path,
    param,
  }) as ReuseRouteOptions;

export class CustomReuseStrategy implements RouteReuseStrategy {
  routesForUpdate: ReuseRouteOptions[] = [
    makeReuseRouteOption('playlists', 'playlistID'),
    makeReuseRouteOption('films', 'filmID'),
    makeReuseRouteOption('channel', 'creatorID=31'),
    makeReuseRouteOption('chat', 'threadId'),
  ];
  storedRouteHandles = new Map<string, DetachedRouteHandle>();

  // Decides if the route should be stored
  shouldDetach(route: ActivatedRouteSnapshot): boolean {
    return (
      this.routesForUpdate.findIndex(
        (option) => option.path === route.routeConfig.path,
      ) > -1
    );
  }

  // Store the information for the route we're destructing
  store(route: ActivatedRouteSnapshot, handle: DetachedRouteHandle): void {
    this.storedRouteHandles.set(route.routeConfig.path, handle);
  }

  // Return true if we have a stored route object for the next route
  shouldAttach(route: ActivatedRouteSnapshot): boolean {
    return (
      route.routeConfig && this.storedRouteHandles.has(route.routeConfig.path)
    );
  }

  // If we returned true in shouldAttach(), now return the actual route data for restoration
  retrieve(route: ActivatedRouteSnapshot): DetachedRouteHandle {
    return this.storedRouteHandles.get(route.routeConfig.path);
  }

  // Reuse the route if we're going to and from the same route
  shouldReuseRoute(
    future: ActivatedRouteSnapshot,
    curr: ActivatedRouteSnapshot,
  ): boolean {
    const isRouteShouldReuse = this.routesForUpdate.some(
      (option) => this.checkIsRouteShouldReuse(future, curr, option) === false,
    );

    if (!isRouteShouldReuse) {
      return false;
    }
    // If we are on Film or Playlist page, we check is change id
    // If id was changed - we restart component without url reloading
    // (because query changing does not trigger page restart by default).
    // In all the other cases - do default behaviour.
    // Read more: https://stackoverflow.com/questions/41280471/how-to-implement-routereusestrategy-shoulddetach-for-specific-routes-in-angular/58737722#58737722

    return future.routeConfig === curr.routeConfig;
  }

  checkIsRouteShouldReuse(
    future: ActivatedRouteSnapshot,
    curr: ActivatedRouteSnapshot,
    option: ReuseRouteOptions,
  ): boolean {
    const isCurrentPath = future.routeConfig?.path === option.path;
    const isParamChanged =
      future.queryParams?.[option.param] !== curr.queryParams?.[option.param];
    return !(isCurrentPath && isParamChanged);
  }
}
